import React, { JSX, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import './Checkout.css';
import { toast } from 'react-toastify';
import { Routes } from '../../app/Routes';
import { BlechconOrder } from '../../blechcon';
import { CheckoutSteps } from '../../components/CheckoutSteps/CheckoutSteps';
import { Modal } from '../../components/Modal';
import { Page } from '../../components/Page/Page';
import { localized } from '../../config/localization';
import { AuthContext, AuthContextProps } from '../../services/AuthContext';
import { fetchOrder, submitOrder, updateOrder } from '../../services/orderApi';
import { useUserInfo } from '../../services/useUserInfo';
import { useConfiguration } from './useConfiguration';

type Props = {
  readonly component: React.ComponentType<any>;
};

type ParamsType = {
  id: string | undefined;
};

export function Checkout({ component }: Props): JSX.Element {
  const navigate = useNavigate();
  const [userInfo] = useUserInfo();
  const configuration = useConfiguration();

  const { isAdmin } = useContext<AuthContextProps>(AuthContext);

  const { id = null } = useParams<ParamsType>();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [persistPending, setPersistPending] = useState(false);
  const [order, setOrder] = useState<BlechconOrder | null>(null);

  useEffect(() => {
    if (userInfo?.invoiceAddresses.length === 0) {
      setIsModalVisible(true);
    }
  }, [userInfo]);

  useEffect(() => {
    if (id) {
      setOrderLoading(true);
      fetchOrder(id)
        .then((response) => {
          setOrder(response);
        })
        .catch((error) => {
          if (error.response && [403, 404].includes(error.response.status)) {
            toast.error(localized.ORDER_NOT_FOUND);
            setNotFound(true);
          } else {
            toast.error(`${localized.GENERAL_ERROR}: ${String(error)}`);
          }
        })
        .finally(() => {
          setOrderLoading(false);
        });
    } else {
      setOrder(null);
    }
  }, [id]);

  //TODO: promise BlechconOrder zurückgeben oder error werfen, aber nicht null
  const persistOrder = useCallback(async (orderToUpdate: Partial<BlechconOrder>): Promise<BlechconOrder | null> => {
    setPersistPending(true);
    try {
      const updatedOrder = await updateOrder(orderToUpdate, orderToUpdate.id || null);
      setOrder(updatedOrder);
      return updatedOrder;
    } catch (error) {
      toast.error(String(error));
    } finally {
      setPersistPending(false);
    }
    return null;
  }, []);

  const finalizeOrder = useCallback(
    async (orderToUpdate: Partial<BlechconOrder>): Promise<BlechconOrder | null> => {
      const persistedOrder = await persistOrder(orderToUpdate);

      setPersistPending(true);

      try {
        const updatedOrder = await submitOrder(persistedOrder?.id || '');
        setOrder(updatedOrder);
        return updatedOrder;
      } catch (error) {
        toast.error(String(error));
      } finally {
        setPersistPending(false);
      }
      return null;
    },
    [persistOrder]
  );

  const pageIsLoading =
    (userInfo === null || configuration === null || orderLoading || (id !== null && order === null)) && !notFound;

  if (order && order?.orderStatus === 'submitted' && !shouldSkipSubmitCheck()) {
    if (isAdmin) {
      return <Navigate to={Routes.adminOrderDetails(order.id)} />;
    } else {
      return <Navigate to={Routes.customerOrdersDetails(order.id)} />;
    }
  }

  return (
    <Page title={localized.CALCULATOR_V2} loading={pageIsLoading}>
      {isModalVisible && (
        <Modal
          onSuccess={{
            label: localized.TO_SETTINGS,
            execute: () => {
              navigate(Routes.userSettings());
            },
          }}
        >
          <p className="checkout__modal-label">{localized.INVOICE_ADDRESS_NEEDED}</p>
          <p className="checkout__modal-label">
            {localized.INVOICE_ADDRESS_NEEDED_EXPLANATION} <strong>{localized.SETTINGS}</strong>.
          </p>
        </Modal>
      )}

      <CheckoutSteps />
      {!notFound &&
        React.createElement(component, {
          key: id || Date.now(), // geht nicht, wenn id schon undefined ist
          userInfo,
          configuration,
          order,
          persistPending,
          persistOrder,
          finalizeOrder,
          isAdmin,
        })}
    </Page>
  );
}

function getSkipSubmitCheckStorageKey(): string {
  return 'SuccessPage_SkipSubmitCheck';
}

export function skipSubmitCheck() {
  sessionStorage.setItem(getSkipSubmitCheckStorageKey(), 'true');
}

export function clearSkipSubmitCheck() {
  sessionStorage.removeItem(getSkipSubmitCheckStorageKey());
}

/*
 * In React Router v6 über den State geregelt. In React Router v7 geht es nicht, wenn vom Parent
 * auf den State zugegriffen wird.
 */
export function shouldSkipSubmitCheck(): boolean {
  try {
    const flag = sessionStorage.getItem(getSkipSubmitCheckStorageKey());
    return flag === 'true';
  } catch {
    return false;
  }
}
